import { AlertTriangle, Bot } from "lucide-react";

import { Badge } from "@/components/ui/badge";

import type { WhatsAppBroadcastRecipient } from "../types";
import {
  daysInStage,
  deriveRecipientStage,
  isLikelyAutoResponder,
  recipientDisplayName,
  recipientFlag,
} from "./broadcastBoard";

export function BroadcastBoardCard({
  recipient,
  onSelect,
}: {
  recipient: WhatsAppBroadcastRecipient;
  onSelect: (recipient: WhatsAppBroadcastRecipient) => void;
}) {
  const { key: stage, enteredAt } = deriveRecipientStage(recipient);
  const days = daysInStage(enteredAt);
  const flag = recipientFlag(recipient.phone_country_iso2);
  const reply = recipient.reply_text?.trim();
  const autoReply = isLikelyAutoResponder(reply);
  const confidence = recipient.attribution_confidence;
  const hasError = stage === "failed";

  return (
    <button
      type="button"
      onClick={() => onSelect(recipient)}
      className="w-full rounded-md border bg-card p-3 text-left transition-colors hover:bg-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      <div className="flex items-center justify-between gap-2">
        <span className="truncate text-sm font-medium">
          {flag ? `${flag} ` : ""}
          {recipientDisplayName(recipient)}
        </span>
        {hasError ? (
          <AlertTriangle
            className="size-4 shrink-0 text-destructive"
            aria-label="Failed"
          />
        ) : null}
      </div>

      {reply ? (
        <p
          className="mt-1.5 line-clamp-2 text-xs text-muted-foreground"
          title={reply}
        >
          {reply}
        </p>
      ) : null}

      <div className="mt-1.5 flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
        {autoReply ? (
          <Badge variant="outline" className="gap-1 font-normal">
            <Bot className="size-3" aria-label="Auto-reply" />
            auto-reply
          </Badge>
        ) : null}
        {days != null ? <span className="tabular-nums">{days}d</span> : null}
        {confidence && confidence !== "high" ? (
          <span className="capitalize">{confidence} confidence</span>
        ) : null}
      </div>
    </button>
  );
}
